import {
  Component,
  DestroyRef,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  output,
  signal, ChangeDetectionStrategy} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Module } from 'utils/interfaces/api-dtos.util';
import { MatButtonModule } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { IssueTypeStore } from '../../../stores/issue-type.store';
import { MatDialogContent, MatDialogModule } from '@angular/material/dialog';
import { ModuleStore } from '../../../stores/module.store';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import {
  FormMode,
  IssueTypeWithFormData,
  IssueWithFormData,
} from 'utils/interfaces/form.util';
import {
  IssueCreatedEvent,
  RenderIssueTypeComponent,
} from '../../issue-manager/render-issue-type/render-issue-type.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ImportIssuesComponent } from '../import-issues/import-issues.component';
import { CustomSnackBarComponent } from '../../custom-snack-bar/custom-snack-bar.component';
import { IssueTypeCategoryStore } from '../../../stores/issue-type-category.store';
import { IssueTypeCategoryEnum } from 'utils/enums/issue-type-category.enum';
import { AccessStore } from '../../../stores/access.store';
import { Router } from '@angular/router';
import { ContextFilterService } from '../../../services/context-filter.service';
import { isEmpty } from 'lodash';
import { PERMISSION } from 'utils/permissions/permission-catalog';

@Component({
    selector: 'gcp-workspace-create-issue-modal',
    imports: [
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogContent,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    ReactiveFormsModule,
    MatInputModule,
    RenderIssueTypeComponent,
    MatIconModule,
    MatTabGroup,
    MatTab,
    ImportIssuesComponent
],
    providers: [],
    templateUrl: './create-issue-modal.component.html',
    styleUrl: './create-issue-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateIssueModalComponent implements OnInit {
  closeDialog = output<boolean>();
  formGroup: FormGroup | undefined;
  moduleData: Observable<Module[]> = of([]);
  formArray: FormArray;
  FormMode = FormMode;
  
  moduleStore = inject(ModuleStore);
  private readonly accessStore = inject(AccessStore);
  issueTypeStore = inject(IssueTypeStore);
  issueTypeCategoryStore = inject(IssueTypeCategoryStore);
  contextFilterService = inject(ContextFilterService);
  issueTypeData = signal<IssueTypeWithFormData[]>([]);

  issueTypeId: WritableSignal<string | null> = signal(null);
  moduleId: WritableSignal<string | null> = signal(null);
  public allowedModules: Set<Module> = new Set();
  public isChangedIssueType = false;

  private readonly formBuilder = inject(FormBuilder);
  public readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.formArray = this.formBuilder.array([]);

    effect(
      () => {
        if (!this.isChangedIssueType) {
          if (this.currentModule()) {
            this.moduleId.set(this.currentModule()?.id ?? null);
            this.moduleDropdownControl?.setValue(this.currentModule()?.id, { emitEvent: false });
          }
          const issueTypes = this.issueTypesCurrentModule();
          if (issueTypes) {
            const issueTypeCategories = issueTypes?.map(
              (issueType) =>
                this.issueTypeCategoryStore.entityMap()[
                  issueType.issueTypeCategoryId
                ]
              );
              const issueTypeCategoryData = issueTypeCategories?.filter(
                (data) => data?.name !== IssueTypeCategoryEnum.SUB_TASK
              );
              this.issueTypeData.set(issueTypes?.filter((issueType) =>
                issueTypeCategoryData.some(
                  (issueTypeCategory) =>
                    issueType.issueTypeCategoryId === issueTypeCategory?.id
                )
              ));
            const issueTypeTaskCategoryId = issueTypeCategories?.find(
              (data) => data?.name === IssueTypeCategoryEnum.TASK
            )?.id;
            const taskId = issueTypes?.find(
              (issueType) => issueType.id === issueTypeTaskCategoryId
            )?.id;
            if (taskId) {
              this.issueTypeId.set(taskId);
              this.issueTypeDropdownControl?.setValue(taskId, { emitEvent: false });
            }
          }
        }
      }
    );

    effect(
      () => {
        const moduleId = this.moduleId();
        if (moduleId) {
          const issueTypeCategories = this.moduleStore
            .entityMap()
          [moduleId].IssueType.map(
            ({ issueTypeCategoryId }) =>
              this.issueTypeCategoryStore.entityMap()[issueTypeCategoryId]
          );
          const issueTypesNotSubtask = issueTypeCategories?.filter(
            (data) => data?.name !== IssueTypeCategoryEnum.SUB_TASK
          );
          if (this.issueTypes()) {
            this.issueTypeData.set(
              this.issueTypes()?.filter((issueTypeCategory) =>
                issueTypesNotSubtask.some(
                  (notSubtask) =>
                    issueTypeCategory?.issueTypeCategoryId === notSubtask?.id
                )
              ) ?? []
            );
          }
        }
      }
    );
  }

  issuesCreated: IssueCreatedEvent = { created: [], showSnackbar: true };

  currentModule = computed(() => this.moduleStore.currentModule());

  issueTypesCurrentModule = computed(
    () => this.currentModule()?.IssueType ?? null
  );

  issueTypes = computed(
    () => this.moduleStore.entityMap()[this.moduleId() ?? '']?.IssueType ?? null
  );

  // Alphabetical lists for the dropdowns so names are easy to scan/find.
  sortedIssueTypeData = computed(() =>
    [...this.issueTypeData()].sort((a, b) => a.name.localeCompare(b.name))
  );

  get sortedModules(): Module[] {
    return Array.from(this.allowedModules).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  ngOnInit(): void {
    this.checkModuleAccess();
    this.createForm();
    this.issueTypeDropdownControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.isChangedIssueType = true;
      this.issueTypeId.set(value);
    }
    );
    this.moduleDropdownControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.moduleId.set(value);
      this.issueTypeId.set(null);
    });
    this.issueTypeCategoryStore.loadAll().subscribe();
  }

  createForm() {
    this.formGroup = this.formBuilder.group({
      module: [this.currentModule()?.id, Validators.required],
      issueType: [null, Validators.required],
    });
  }

  onIssuesCreatedChange(event: IssueCreatedEvent) {
    this.issuesCreated = event;
    let message: string;
    if (this.issuesCreated.showSnackbar) {
      const contextFilter = this.getContextFilterQueryParam();
      if (this.issuesCreated.created.length === 1) {
        const createdIssue = this.issuesCreated.created[0];
        const issueKey = this.getKeyForIssue(createdIssue);
        const url = contextFilter
          ? `#/issue/${issueKey}?contextFilter=${encodeURIComponent(contextFilter)}`
          : `#/issue/${issueKey}`;
        message = `Issue <a class="custom-snack-bar-link" href="${url}" target="_blank">(${issueKey}) ${createdIssue.formData.data.title}</a> has been created`;
      } else {
        const issueArr = event.created;
        const modulePrefix = this.getModulePrefixForIssues(issueArr);
        const prefix = this.createUrlForCreatedIssueKeys(
          issueArr.map(this.getKeyForIssue)
        );
        const url = contextFilter
          ? `${modulePrefix}?prefix=${prefix}&contextFilter=${encodeURIComponent(contextFilter)}`
          : `${modulePrefix}?prefix=${prefix}`;
        message = `${issueArr.length} issues created. <a class="custom-snack-bar-link" href="${url}" target="_blank">Click to see created issues</a>`;
      }
      this.showSnackBar(message);
      this.closeDialog.emit(true);
    }
  }

  private getContextFilterQueryParam(): string | null {
    // First try to get from service (most reliable)
    const contextFilterParams = this.contextFilterService.params$.getValue();
    if (!isEmpty(contextFilterParams) && 'organizationId' in contextFilterParams) {
      return JSON.stringify(contextFilterParams);
    }
    // Fallback to router query params in case service hasn't synced
    const queryParams = this.router.routerState.snapshot.root.queryParams;
    const contextFilterFromUrl = queryParams['contextFilter'];
    if (contextFilterFromUrl) {
      return contextFilterFromUrl;
    }
    return null;
  }

  getKeyForIssue(issue: IssueWithFormData) {
    return `${issue.urlPrefix}-${issue.urlSuffix}`;
  }

  getModulePrefixForIssues(issues: IssueWithFormData[]) {
    const set: Set<string> = new Set();
    for (const issue of issues) {
      set.add(issue.urlPrefix);
    }
    return set.size > 1 ? '#/all-issues' : `#/issues/${set.values().next().value}`;
  }

  createUrlForCreatedIssueKeys(keys: string[]): string {
    return encodeURIComponent(
      JSON.stringify({
        filterType: 'text',
        operator: 'OR',
        conditions: keys.map((key) => ({
          filterType: 'text',
          type: 'contains',
          filter: key,
        })),
      })
    );
  }

  private showSnackBar(message: string): void {
    this.snackBar.openFromComponent(CustomSnackBarComponent, {
      duration: 8000,
      data: message,
      panelClass: ['snackbar-success'],
    });
  }

  get moduleDropdownControl() {
    return this.formGroup?.get('module');
  }

  get issueTypeDropdownControl() {
    return this.formGroup?.get('issueType');
  }

  get formValid(): boolean {
    return !!this.formGroup?.valid;
  }

  checkModuleAccess() {
    for (const module of this.moduleStore.entities()) {
      const moduleExists = Array.from(this.allowedModules).some(
        (existing) => existing.name === module.name
      );
      // Permission-driven (roles overhaul §11.2): creatable org-wide via the
      // base role, or in this specific module via a module role binding.
      if (!moduleExists && this.accessStore.can(PERMISSION.issues.create, module.id)) {
        this.allowedModules.add(module);
      }
    }
  }
}
